import React, { FC, MutableRefObject } from 'react';
import classNames from 'classnames';
import { makeStyles } from 'tss-react/mui';

import { transactionsTypes } from 'companion-app-components/flux/transactions';

import AmountField from 'components/QuickenControls/AmountField';

import { RegListItem, styles } from '../styles';

import type { AttributesType, CallbacksType } from '../types';

const useStyles = makeStyles()(styles as Record<string, any>);

// Handles the interplay between MoneyOut and MoneyIn fields
// What we are going for is a seamless usage of these fields across a single transaction amount
// First, since we use an AmountField component both for the static and editable states, we need
// to manage the values/properties appropriatly based on the value of the transaction. We also want
// behavior to be elegant, so when the user starts typing in one field, the other field blanks out
// (but not before the user types).  WE use the '&' character to represent zero/blank fields, which
// provides an actual value, but gets validated into a null.  Null itself would prevent a component update
//

interface DoubleAmountFieldProps {
  attributes: AttributesType;
  callbacks: CallbacksType;
  editable: boolean;
  SHOW_ALL: boolean;
  txn: transactionsTypes.CashFlowTransaction;
  currencySymbol: string;
  amountEditable: boolean | string;
  currEditFieldName: string;
  field: string;
  enableAutoFocus: boolean;
  focusField: MutableRefObject<HTMLAnchorElement | null>;
}

const DoubleAmountField: FC<DoubleAmountFieldProps> = ({
  attributes,
  callbacks,
  editable,
  SHOW_ALL,
  txn,
  currencySymbol, // state
  amountEditable,
  currEditFieldName,
  field,
  enableAutoFocus,
  focusField,
}) => {
  const { classes, theme } = useStyles();

  const thisFieldActive: boolean = (amountEditable && editable && (currEditFieldName === field)) as boolean;

  let value: string | number = '0';
  if (thisFieldActive) {
    // we need to just pass through the current txn amount string
    const amountValid = (field === 'expense') ? txn.amount <= 0 : txn.amount > 0;
    value = amountValid ? txn.amount : '&';

  } else {
    const amountValid = (field === 'expense') ? txn.amount <= 0 : txn.amount > 0;
    value = amountValid ? Math.abs(txn.amount) : '&';
  }
  const zeroIsBlank = txn.amount === 0 ? (field !== 'expense') : true;

  return (
    <RegListItem
      {...attributes}
      className={classNames('amount', classes.regItem, 'protectWidth', thisFieldActive ? 'editable' : '')}
    >
      {SHOW_ALL &&
      <AmountField
        // @ts-expect-error: Property 'register' does not exist on type 'Components<BaseTheme>'.
        disableUnderline={theme.components?.register.noUnderline}
        clickable={amountEditable ? 'true' : null}
        amountType={field}
        onChange={callbacks.handleChange}
        onBlur={(e) => {
          callbacks.onAmountBlur?.(e, field);
        }}
        editable={thisFieldActive}
        autoFocus={enableAutoFocus}
        // @ts-expect-error: Property 'register' does not exist on type 'Components<BaseTheme>'.
        fontSize={theme.components?.register.fontSize.default}
        // @ts-expect-error: Type 'string' is not assignable to type 'number'.
        value={String(value)}
        color={field === 'expense' ? 'black' : 'green'}
        useAbsoluteValue={field === 'expense'}
        zeroIsBlank={zeroIsBlank}
        className={classNames(classes.regInput, 'amount')}
        currencySymbol={currencySymbol}
        // @ts-expect-error: null is not assignable to type
        inputRef={thisFieldActive ?
          (input) => {
            // @ts-expect-error: inputRef provides HTMLInputElement but focusField is typed as HTMLAnchorElement
            focusField.current = input; // eslint-disable-line
          } : null}
      >
      </AmountField>}
    </RegListItem>
  );
};

export default DoubleAmountField;
